import { Configuration, genericFileApi } from "@interface48/api";
import { formatFileSize } from "@interface48/formatting";
import LinearProgress from "@material-ui/core/LinearProgress";
import { createStyles, Theme, withStyles, WithStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import classNames from "classnames";
import React from "react";
import Dropzone, { DropEvent } from "react-dropzone";
import { FileDetails, FilePickerOptions } from "./models";
import { getFileDescription } from "./utils";

const styles = (theme: Theme) =>
  createStyles({
    root: {
      marginTop: theme.spacing(3),
    },
    existingFilePicker: {
      marginTop: theme.spacing(2),
    },
    displayField: {
      marginTop: theme.spacing(1.5),
      paddingBottom: 0,
    },
    fileDescription: {
      marginBottom: theme.spacing(1),
    },
    fileUploadRegion: {
      cursor: "pointer",
      padding: theme.spacing(2),
      // Border Colour to match Input field underlines, as per
      // https://github.com/mui-org/material-ui/blob/master/packages/material-ui/src/Input/Input.js#L9
      border: `solid 1px ${theme.palette.type === "light" ? "rgba(0, 0, 0, 0.42)" : "rgba(255, 255, 255, 0.7)"}`,
      borderRadius: theme.shape.borderRadius,
      backgroundColor: theme.palette.background.paper,
    },
    fileUploadRegionError: {
      borderColor: theme.palette.error.main,
      color: theme.palette.error.main,
    },
    fileUploadRegionDisabled: {
      cursor: "default",
    },
    progress: {
      height: 4,
      marginTop: -4,
      borderBottomLeftRadius: theme.shape.borderRadius,
      borderBottomRightRadius: theme.shape.borderRadius,
    },
  });

interface FilePickerProps extends WithStyles<typeof styles> {
  id: string;
  fileDetails?: {
    fileName: string;
    fileSizeBytes: number;
  };
  options: FilePickerOptions;
  errors?: string[];
  className?: string;
  getApiConfiguration?: () => Promise<Configuration>;
  onChange: (fileUploadId: string, fileDetails: { fileName: string; fileSizeBytes: number }) => void;
}

interface FilePickerState {
  fileUploadId?: string;
  fileDetails?: FileDetails;
  uploadProgress?: number;
  transientError?: string;
}

export const FilePickerComponent = class extends React.Component<FilePickerProps, FilePickerState> {
  public transientErrorTimeoutId: number | undefined;

  constructor(props: FilePickerProps) {
    super(props);

    const { fileDetails } = this.props;

    this.state = { fileDetails };
  }

  public render() {
    const { id, options, errors, className, classes } = this.props;
    const { acceptedMimeTypes, maxSizeBytes } = options;
    const { fileDetails, uploadProgress, transientError } = this.state;

    return (
      <>
        <div className={classNames(classes.root, className)}>
          {fileDetails && (
            <div className={classes.fileDescription}>
              {getFileDescription(fileDetails.fileName, fileDetails.fileSizeBytes)}
            </div>
          )}
          <Dropzone
            disabled={!!transientError}
            onDrop={this.handleAcceptedFiles}
            accept={acceptedMimeTypes}
            maxSize={maxSizeBytes}
            multiple={false}
          >
            {({ getRootProps, getInputProps }) => (
              <div {...getRootProps()}>
                <input id={id} {...getInputProps()} />
                <Typography
                  component="div"
                  className={classNames(
                    classes.fileUploadRegion,
                    !!transientError && classes.fileUploadRegionDisabled,
                    (!!transientError || !!(errors && errors.length)) && classes.fileUploadRegionError,
                  )}
                >
                  {transientError || "Drag & drop file here, or click to select file..."}
                </Typography>
              </div>
            )}
          </Dropzone>
        </div>
        {uploadProgress != null && (
          <LinearProgress variant="determinate" className={classes.progress} value={uploadProgress} />
        )}
      </>
    );
  }

  public handleAcceptedFiles = (acceptedFiles: File[], rejectedFiles: File[], event: DropEvent) => {
    const { options, getApiConfiguration, onChange } = this.props;
    const { uploadUrl, fileNameScheme, acceptedMimeTypes, maxSizeBytes } = options;

    if (!uploadUrl) {
      throw Error("uploadUrl required on ui:options.");
    }

    // If more than one conforming files have been uploaded...
    if (acceptedFiles.length > 1) {
      this.showTransientError("You must upload multiple Files separately.");
    } else if (acceptedFiles.length === 1) {
      const file = acceptedFiles[0];

      if (!getApiConfiguration) {
        throw Error("No API Configuration resolver was provided for File Upload.");
      }

      let illegalFileNameCharacters: string | undefined;

      // If we must check to ensure the FileName adheres to a specified Scheme...
      if (fileNameScheme) {
        let illegalFileNameCharacterMatches: RegExpMatchArray | null | undefined;
        // Reference: https://support.office.com/en-us/article/invalid-file-names-and-file-types-in-onedrive-onedrive-for-business-and-sharepoint-64883a5d-228e-48f5-b3d2-eb39e07630fa#invalidcharacters
        // If SharePoint 2013...
        if (fileNameScheme === "sp2013") {
          illegalFileNameCharacterMatches = file.name.match(/([~"#%&*:<>?/\\{|}])/g);
        }
        // Otherwise, if SharePoint Online...
        else if (fileNameScheme === "spo") {
          illegalFileNameCharacterMatches = file.name.match(/(["*:<>?/\\|])/g);
        }
        // Otherwise, this is an unknown scheme...
        else {
          throw Error(`fileNameScheme '${fileNameScheme}' is not supported.`);
        }

        // If there are one or more illegal characters, serialize to a CSV string...
        if (illegalFileNameCharacterMatches) {
          illegalFileNameCharacters = illegalFileNameCharacterMatches
            .filter((v, i, a) => a.indexOf(v) === i)
            .join(", ");
        }
      }

      if (illegalFileNameCharacters) {
        this.showTransientError(`File Name contains the following illegal character(s): ${illegalFileNameCharacters}`);
      } else {
        genericFileApi
          .post(uploadUrl, file, this.handleFileUploadProgress, getApiConfiguration)
          .then(fileUploadId => {
            const fileDetails = {
              fileName: file.name,
              fileSizeBytes: file.size,
            };

            this.setState({ ...this.state, fileUploadId, fileDetails, uploadProgress: undefined }, () => {
              onChange(fileUploadId, fileDetails);
            });
          })
          .catch(error => {
            this.setState({ ...this.state, uploadProgress: undefined }, () => {
              console.error(error);

              this.showTransientError("An error occurred upon attempting to upload the File. Please try again.");
            });
          });

        this.setState({ ...this.state, uploadProgress: 0 });
      }
    } else if (rejectedFiles.length) {
      // Generate validation errors based on the first file only for now
      const file = rejectedFiles[0];
      const fileValidationErrors: string[] = [];

      if (acceptedMimeTypes && acceptedMimeTypes.indexOf(file.type) === -1) {
        fileValidationErrors.push(`${file.type} file types are not accepted`);
      }
      if (maxSizeBytes && file.size > maxSizeBytes) {
        fileValidationErrors.push(`${formatFileSize(file.size)} exceeds the maximum allowable file size`);
      }

      const fileValidationError = fileValidationErrors.length
        ? `${fileValidationErrors.join(" and ")}.`
        : "File not accepted for some unspecified reason";

      this.showTransientError(fileValidationError);
    }
  };

  public handleFileUploadProgress = (progress: number) => {
    this.setState({ ...this.state, uploadProgress: progress });
  };

  public showTransientError = (transientError: string) => {
    this.transientErrorTimeoutId = window.setTimeout(this.hideTransientError, 3000);

    this.setState({ ...this.state, transientError });
  };

  public hideTransientError = () => {
    if (this.transientErrorTimeoutId) {
      window.clearTimeout(this.transientErrorTimeoutId);

      this.transientErrorTimeoutId = undefined;
    }

    this.setState({ ...this.state, transientError: undefined });
  };
};

export const FilePicker = withStyles(styles)(FilePickerComponent);
